import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { AccountNote } from '../../../types';
import { apiService } from '../../../services/api';
import { useAppSelector } from '../../../redux/hooks';
import { formatDate } from '../../../utils/helpers';

interface AccountNotesProps {
  accountID: string;
}

const AccountNotes: React.FC<AccountNotesProps> = ({ accountID }) => {
  const [notes, setNotes] = useState<AccountNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [fadeIn, setFadeIn] = useState(false);

  // Get current user from Redux store
  const { user } = useAppSelector((state) => state.auth);

  // Load notes on component mount
  useEffect(() => {
    loadNotes();
  }, [accountID]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate minimum loading time for smooth UX
      const [notesData] = await Promise.all([
        apiService.getAccountNotes(accountID),
        new Promise(resolve => setTimeout(resolve, 800)) // Minimum 800ms loading
      ]);
      
      setNotes(notesData);
      
      // Trigger fade-in animation
      setTimeout(() => setFadeIn(true), 100);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('Failed to load account notes');
      setFadeIn(true); // Still show the component even if there's an error
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = () => {
    if (!newNoteContent.trim()) {
      setSnackbarMessage('Please enter a note before submitting');
      setSnackbarOpen(true);
      return;
    }
    
    if (!user) {
      setSnackbarMessage('User information not available');
      setSnackbarOpen(true);
      return;
    }
    
    setShowConfirmDialog(true);
  };

  const confirmAddNote = async () => {
    if (!user) return;
    
    try {
      setSubmitting(true);
      
      const newNote: Omit<AccountNote, 'id' | 'noteId' | 'timestamp'> = {
        accountID,
        userID: user.id,
        userName: user.username,
        content: newNoteContent.trim()
      };
      
      const createdNote = await apiService.addAccountNote(newNote);
      
      // Add the new note to the beginning of the list
      setNotes(prev => [createdNote, ...prev]);
      
      // Clear the form
      setNewNoteContent('');
      setShowConfirmDialog(false);
      
      setSnackbarMessage('Note added successfully');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error adding note:', err);
      setSnackbarMessage('Failed to add note');
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }
    
    try {
      const success = await apiService.deleteAccountNote(noteId);
      
      if (success) {
        setNotes(prev => prev.filter(note => note.noteId !== noteId));
        setSnackbarMessage('Note deleted successfully');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Failed to delete note');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error deleting note:', err);
      setSnackbarMessage('Failed to delete note');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Show loading screen
  if (loading) {
    return (
      <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          Account Notes
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2
        }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading notes...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Fade in={fadeIn} timeout={600}>
      <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          Account Notes
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Add New Note Section */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            placeholder="Add a note about this account..."
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNote}
            disabled={!newNoteContent.trim() || !user}
            sx={{ minWidth: 120 }}
          >
            Add Note
          </Button>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {/* Notes List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {notes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No notes yet. Add the first note above.
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notes.map((note, index) => (
                <ListItem
                  key={note.noteId}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 2,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {/* Note Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={<PersonIcon />}
                        label={note.userName}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        icon={<TimeIcon />}
                        label={formatDate(note.timestamp)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    
                    {/* Only show delete button if current user created the note */}
                    {user && user.id === note.userID && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteNote(note.noteId)}
                        color="error"
                        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  
                  {/* Note Content */}
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.5,
                    }}
                  >
                    {note.content}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        
        {/* Confirmation Dialog */}
        <Dialog
          open={showConfirmDialog}
          onClose={() => !submitting && setShowConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Note</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please review your note before adding it:
            </Typography>
            <Paper
              sx={{
                p: 2,
                mt: 2,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.5,
                }}
              >
                {newNoteContent}
              </Typography>
            </Paper>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Posted by:
              </Typography>
              <Chip
                icon={<PersonIcon />}
                label={user?.username || 'Unknown User'}
                size="small"
                variant="outlined"
                color="primary"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowConfirmDialog(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAddNote}
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {submitting ? 'Adding...' : 'Add Note'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
        />
      </Paper>
    </Fade>
  );
};

export default AccountNotes;
